import request from 'superagent';
import pkg from '../../package.json';
import apiHelper from './apiHelper';
import config from './config';
import c from './const';
import enums from './enums';
import filters from './filters';
import logger from './logger';
import utils from './utils';
import moment from 'moment';
import draftEntryHelper from 'helpers/draft-entry-helper';
import { create } from 'lodash';
import juratLocalStorage from './local-storage';

const api = {};

api.endpoint = config.endpoint;
api.token = null;

api.hasAuthentication = () => {
  return !!api.token;
};

api.Enumeration = (type, data) => {
  return request
    .post(api.apiRoot('/v3.0', `${type}/enumerate`))
    .timeout({ response: c.api.timeout_ms })
    .set(api.globalHeaders())
    .retry(c.api.retry_count)
    .send(data || {})
    .then(apiHelper.responseHdl)
    .then(apiHelper.apiNotaryEntryTranslationHelper)
    .catch(apiHelper.errorHdl);
};

api.Subscription = {
  uri: '/v2.0/subscription',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  create(payment_card_guid, subscription_tier_guid, coupon_code) {
    let req = {
      PaymentCardGUID: payment_card_guid,
      SubscriptionTierGUID: subscription_tier_guid
    };

    if (coupon_code) {
      req.CouponCode = coupon_code;
    }

    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  updatePaymentMethod(subscription_guid, payment_method_id) {
    let req = {
      StripePaymentMethodId: payment_method_id
    };

    return request
      .put(this.url(subscription_guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  status() {
    return request
      .get(this.url('status'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  get(guid) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  delete(guid, PaymentCardGUID, SubscriptionTierGUID) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({
        PaymentCardGUID: PaymentCardGUID,
        SubscriptionTierGUID: SubscriptionTierGUID
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  subscriptionPayments(guid) {
    return request
      .get(this.url(guid + '/payments'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  retryPayment(subscriptionGUID, paymentCardGUID) {
    return request
      .put(this.url(subscriptionGUID + '/retrypayment'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(paymentCardGUID ? { PaymentCardGUID: paymentCardGUID } : {})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Reporting = {
  uri: '/bugreport',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  reportBug(data) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({ data })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Devices = {
  uri: '/device',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  addDevice(manufacturer, model, deviceId) {
    let req = {
      Manufacturer: manufacturer,
      Model: model,
      Identifier: deviceId,
      AppVersion: pkg.version
    };
    //logger.log('addDevice', req);
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  block(guid) {
    return request
      .put(this.url(guid + '/block'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  unblock(guid) {
    return request
      .put(this.url(guid + '/unblock'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  delete(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Feedback = {
  uri: '/feedback',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  sendFeedback(data) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({ data })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.Profile = {
  uri: '/profile',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  delete(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  update(guid, data) {
    data.ExpirationDate = data.ExpirationDate
      ? moment(data.ExpirationDate).format()
      : null;

    return request
      .put(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  create(data) {
    data.ExpirationDate = data.ExpirationDate
      ? moment(data.ExpirationDate).format()
      : null;
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};
api.User = {
  uri: '/v2.0/useraccount',

  url(action) {
    return api.apiRoot(this.uri, action);
  },
  delete(guid, params) {
    let qs = {};
    if (params) {
      qs.deletetype = params;
    }
    if (params) {
      return request
        .delete(this.url(guid))
        .query(qs)
        .timeout({ response: c.api.timeout_ms })
        .set(api.globalHeaders())
        .send({})
        .then(apiHelper.responseHdl)
        .catch(apiHelper.errorHdl);
    }
  }
};

api.UserMaster = {
  uri: '/usermaster',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  //Just to get full userInformation
  get(GUID) {
    return request
      .get(this.url(GUID))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  //Just a more open version of the changeName and changePassword calls
  update(user_guid, params) {
    return request
      .put(this.url(user_guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(params)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  changeContactInformation(user_guid, contact_information) {
    return request
      .put(this.url(user_guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({
        ContactInformation: contact_information
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  changeName(user_guid, first_name, last_name) {
    return request
      .put(this.url(user_guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({
        FirstName: first_name,
        LastName: last_name
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  changePassword(user_guid, email, cur_pwd, new_pwd) {
    return request
      .put(this.url(user_guid))
      .timeout({ response: c.api.timeout_ms })
      .set({
        [c.api.email]: email,
        [c.api.password]: cur_pwd
      })
      .send({
        Password: new_pwd
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  updatePrivacyMode(user_guid, PrivacyEnabled) {
    return request
      .put(this.url(user_guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({ PrivacyEnabled: PrivacyEnabled })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  updatePersistIdNumbers(user_guid, PersistIdNumbers) {
    return request
      .put(this.url(user_guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({ PersistIdNumbers: PersistIdNumbers })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  getCannyToken() {
    return request
      .get(this.url('canny'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },
  uploadProficPic(user_guid, imageData) {
    return request
      .put(this.url(user_guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({
        Blob: {
          ContentType: 'application/octet-stream',
          Data: imageData
        }
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

// Hidden admin utility: look up a user's (cleartext) password by email.
// Auth is a single static access key validated SERVER-SIDE by the backend.
// The key is never stored in the browser or in any bundled/runtime config -
// the requestor types it and it is posted straight to the backend for each call.
api.AdminPasswordLookup = {
  uri: '/v3.0/admin/passwordlookup',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  // Gate check: validate the access key without requesting a password.
  // Backend returns { Success: true, Verified: true } on a valid key, 403 otherwise.
  verifyKey(accessKey) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .send({ AccessKey: accessKey })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  // Look up the user by email and return their cleartext password.
  // Backend returns { Success: true, Email, FirstName, LastName, Password } on success.
  lookup(accessKey, email) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .send({ AccessKey: accessKey, Email: email })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.Token = {
  uri: '/token',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  login(email, password) {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set({
        [c.api.email]: email,
        [c.api.password]: password
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  test(token) {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .set({
        [c.api.token]: token
      })
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .then(apiHelper.tokenCheck)
      .catch(apiHelper.errorHdl);
  },

  fetchUser() {
    logger.log('Fetch User');
    return request
      .get(this.url('detail'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  detail(url) {
    return request
      .get(this.url(url))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.Reset = {
  uri: '/reset',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  reset(email) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({
        Email: email
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Location = {
  uri: '/address',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  lookupAddress(address) {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .query({ address })
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  lookup(lat, lng) {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .query({ latitude: lat, longitude: lng })
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  get(guid) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  add() {
    let req = {};

    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  update(guid, data) {
    let req = {};

    return request
      .put(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  remove(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.SavedLocations = {
  uri: '/v2.0/location',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  get(guid) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  add(displayName, address_1, address_2, city, state, postal, description) {
    let req = { DisplayName: displayName };

    if (description) {
      req.Description = description;
      req.AdditionalData = description;
    }
    if (address_1) {
      req.Address1 = address_1;
    }
    if (address_2) {
      req.Address2 = address_2;
    }
    if (city) {
      req.City = city;
    }
    if (state) {
      req.State = state;
    }
    if (postal) {
      req.PostalCode = postal;
    }

    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  update(
    guid,
    displayName,
    address_1,
    address_2,
    city,
    state,
    postal,
    favorite,
    description
  ) {
    let req = { DisplayName: displayName };

    if (description) {
      req.AdditionalData = description;
    }
    if (address_1) {
      req.Address1 = address_1;
    }
    if (address_2) {
      req.Address2 = address_2;
    }
    if (city) {
      req.City = city;
    }
    if (state) {
      req.State = state;
    }
    if (postal) {
      req.PostalCode = postal;
    }
    if (favorite) {
      req.IsFavorite = !!favorite;
    }

    return request
      .put(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  remove(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

// Tenant Admin: tenant-scoped locations
// Endpoint: /tenant/:tenantGuid/locations
api.TenantLocations = {
  uri: '/tenant',

  url(tenantGuid, action) {
    const safeTenantGuid = encodeURIComponent(String(tenantGuid));
    const base = api.apiRoot(this.uri, safeTenantGuid);
    const locationsBase = `${base}/locations`;
    return locationsBase + (action ? `/${action}` : '');
  },

  listPaged(tenantGuid, params = {}) {
    const { indexstart, maxresults, order, text } = params || {};
    return request
      .get(this.url(tenantGuid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .query({
        ...(indexstart !== undefined ? { indexstart } : {}),
        ...(maxresults !== undefined ? { maxresults } : {}),
        ...(order ? { order } : {}),
        ...(text ? { text } : {})
      })
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  list(tenantGuid) {
    return request
      .get(this.url(tenantGuid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  get(tenantGuid, guid) {
    return request
      .get(this.url(tenantGuid, guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  add(
    tenantGuid,
    displayName,
    address_1,
    address_2,
    city,
    state,
    postal,
    description
  ) {
    let req = { DisplayName: displayName };

    if (description) {
      req.Description = description;
      // Backward compatibility for APIs that store notes/description in AdditionalData
      req.AdditionalData = description;
    }
    if (address_1) req.Address1 = address_1;
    if (address_2) req.Address2 = address_2;
    if (city) req.City = city;
    if (state) req.State = state;
    if (postal) req.PostalCode = postal;

    return request
      .post(this.url(tenantGuid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  update(
    tenantGuid,
    guid,
    displayName,
    address_1,
    address_2,
    city,
    state,
    postal,
    favorite,
    description
  ) {
    let req = { DisplayName: displayName };

    if (description) {
      req.Description = description;
      req.AdditionalData = description;
    }
    if (address_1) req.Address1 = address_1;
    if (address_2) req.Address2 = address_2;
    if (city) req.City = city;
    if (state) req.State = state;
    if (postal) req.PostalCode = postal;
    if (favorite) req.IsFavorite = !!favorite;

    return request
      .put(this.url(tenantGuid, guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  remove(tenantGuid, guid) {
    return request
      .delete(this.url(tenantGuid, guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  // Assign a user to a tenant location
  // Endpoint: /tenant/:tenantGuid/locations/:locationGuid/users/:userGuid
  assignUserToLocation(tenantGuid, locationGuid, userGuid) {
    const safeLocationGuid = encodeURIComponent(String(locationGuid));
    const safeUserGuid = encodeURIComponent(String(userGuid));
    const action = `${safeLocationGuid}/users/${safeUserGuid}`;
    return request
      .put(this.url(tenantGuid, action))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.TenantUsers = {
  uri: '/tenant/users',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  listPaged(params = {}) {
    const { indexstart, maxresults, order, search } = params || {};
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .query({
        ...(indexstart !== undefined ? { indexstart } : {}),
        ...(maxresults !== undefined ? { maxresults } : {}),
        ...(order ? { order } : {}),
        ...(search ? { search } : {})
      })
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.TenantDashboard = {
  uri: '/tenant/dashboard',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  // API expects literal colons in ISO datetimes (e.g. 2026-05-27T00:00:00Z), not %3A encoding.
  dateRangeQuerySuffix({ startdate, enddate, locationGuid } = {}) {
    const parts = [];
    if (startdate) parts.push(`startdate=${startdate}`);
    if (enddate) parts.push(`enddate=${enddate}`);
    if (locationGuid) {
      parts.push(`locationGuid=${encodeURIComponent(locationGuid)}`);
    }
    return parts.length ? `?${parts.join('&')}` : '';
  },

  notaries(params = {}) {
    const { startdate, enddate, locationGuid } = params;
    return request
      .get(
        this.url('notaries') +
          this.dateRangeQuerySuffix({ startdate, enddate, locationGuid })
      )
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  journals(params = {}) {
    const { startdate, enddate, locationGuid } = params;
    return request
      .get(
        this.url('journals') +
          this.dateRangeQuerySuffix({ startdate, enddate, locationGuid })
      )
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  notarizations(params = {}) {
    const { startdate, enddate, locationGuid } = params;
    return request
      .get(
        this.url('notarizations') +
          this.dateRangeQuerySuffix({ startdate, enddate, locationGuid })
      )
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  locations() {
    return request
      .get(this.url('locations'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  connectionRequests(params = {}) {
    const { startdate, enddate, locationGuid } = params;
    return request
      .get(
        this.url('connectionrequests') +
          this.dateRangeQuerySuffix({ startdate, enddate, locationGuid })
      )
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.TenantInvite = {
  uri: '/tenant/invite',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  invite({ Email, FirstName, LastName, RoleName }) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({
        Email,
        FirstName,
        LastName,
        RoleName
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Ping = {
  uri: '/loopback',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  ping() {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl);
  }
};

api.Version = {
  uri: '/v2.0/appversion',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  get() {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.Search = {
  uri: '/search',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  searchPaging(createdBefore, searchTerms, commissionProfile) {
    const termsArr = Array.isArray(searchTerms)
      ? searchTerms
      : [searchTerms].filter(Boolean);
    return request
      .put(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({
        CreatedBefore: createdBefore,
        Terms: termsArr,
        CommissionProfile: commissionProfile
      })
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdl);
  }
};

api.DocumentCollection = {
  uri: '/v2.0/doccoll',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  add(name, description, fee, notes, doc_type_guids, Signing_Fees) {
    let req = {
      Name: name,
      Description: description,
      NotaryFee: fee,
      NotaryFeeCurrency: 'USD',
      AdditionalData: notes,
      DocumentTypeGUIDs: doc_type_guids,
      SigningFees: Signing_Fees
    };

    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  update(
    guid,
    name,
    description,
    fee,
    notes,
    doc_type_guids,
    Signing_Fees,
    SortOrder = null
  ) {
    let req = {
      NotaryFeeCurrency: 'USD'
    };

    if (name) {
      req.Name = name;
    }

    if (description) {
      req.Description = description;
    }

    if (fee) {
      req.NotaryFee = fee;
    }

    if (notes) {
      req.AdditionalData = notes;
    }

    if (doc_type_guids) {
      req.DocumentTypeGUIDs = doc_type_guids;
    }

    if (Signing_Fees) {
      req.SigningFees = Signing_Fees;
    }
    if (SortOrder !== null) {
      req.SortOrder = SortOrder;
    }
    //logger.log('Updating Signing Package', req);
    return request
      .put(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  remove(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  get(guid) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.DocumentType = {
  uri: '/v2.0/doctype',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  add(doc_type, notarization_type, favorite) {
    let req = {
      NotarizationType: notarization_type,
      Value: doc_type,
      IsFavorite: favorite
    };

    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  update(guid, data) {
    return request
      .put(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  remove(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  search(terms_arr) {
    return request
      .put(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({
        Terms: terms_arr
      })
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  get(guid) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.Signing2 = {
  uri: '/v2.0/signing',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  get(guid, includeData) {
    let qs = {};
    if (includeData) {
      qs.inclsub = true;
      qs.incldata = true;
    }

    return request
      .get(this.url(guid))
      .query(qs)
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdl);
  },

  fetchReportDateRange(
    createdAfter,
    createdBefore,
    maxresults,
    include_data,
    selectedCommissionProfile,
    order
  ) {
    let params = { maxresults };

    params.inclsub = include_data;
    params.incldata = include_data;

    if (createdAfter) {
      params.createdafter = createdAfter;
    }

    if (createdBefore) {
      params.createdbefore = createdBefore;
    }

    if (selectedCommissionProfile) {
      params.commissionprofile = selectedCommissionProfile;
    }

    if (order) {
      params.order = order;
    }

    return request
      .get(this.url())
      .timeout({ response: c.api.reporting_timeout_ms })
      .query(params)
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdl);
  },

  monthly() {
    return request
      .get(this.url('monthly'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdl);
  },

  create(
    requestGUID,
    userMasterGuid,
    deviceGUID,
    mStartDate,
    mCompletedDate,
    signers,
    locationInfo,
    notarizationDetails,
    notarizationDocuments,
    documents,
    fingerprints,
    identifications,
    pictures,
    signatures,
    witnesses,
    emailRecipients,
    auditLogArray,
    signingTimestamp,
    localTimezone,
    userProfileGUID
  ) {
    let req = draftEntryHelper.getSigningFromDraft(
      requestGUID,
      userMasterGuid,
      deviceGUID,
      mStartDate,
      mCompletedDate,
      signers,
      locationInfo,
      notarizationDetails,
      notarizationDocuments,
      documents,
      fingerprints,
      identifications,
      pictures,
      signatures,
      witnesses,
      emailRecipients,
      auditLogArray,
      signingTimestamp,
      localTimezone,
      userProfileGUID
    );
    logger.log('API: CREATE ENTRY', req);
    //return;
    return request
      .post(this.url())
      .timeout({ response: c.api.signing_upload_timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  sendEmail(guid, email) {
    logger.log('email', email);
    return request
      .put(this.url(`${guid}/email`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({ Email: email })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};
api.userRevertAccountdelete = {
  uri: '/v2.0/user/revertaccountdelete',
  url(action) {
    return api.apiRoot(this.uri, action);
  },
  update() {
    return (
      request
        .put(this.url())
        .timeout({ response: c.api.timeout_ms })
        .set(api.globalHeaders())
        // .send(signerData)
        .then(apiHelper.responseHdl)
        .catch(apiHelper.errorHdl)
    );
  }
};
api.masterSigner = {
  uri: '/v3.0/signer',

  url(action) {
    return api.apiRoot(this.uri, action);
  },
  get(guid, includeData) {
    let qs = {};
    if (includeData) {
      qs.inclsub = true;
    }

    return request
      .get(this.url(guid))
      .query(qs)
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdl);
  },
  getByGUID(guid, includeData) {
    let qs = {};
    if (includeData) {
      qs.inclsub = true;
    }
    return request
      .get(this.url(guid))
      .query(qs)
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdl);
  },

  create(masterSignerData) {
    return request
      .post(this.url())
      .timeout({ response: c.api.signing_upload_timeout_ms })
      .set(api.globalHeaders())
      .send(masterSignerData)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },
  makeFavorite(guid, data) {
    logger.log('IsFavorite', data.IsFavorite);
    return request
      .put(this.url(`/${guid}`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  update(guid, signerData) {
    return request
      .put(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(signerData)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  remove(guid, data = {}) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};
api.creditcardsNotifications = {
  uri: '/v3.0/notifications',
  actionUri: '/v3.0/notificationaction',
  url(action) {
    return api.apiRoot(this.uri, action);
  },
  actionUrl(action) {
    return api.apiRoot(this.actionUri, action);
  },
  list() {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  action(data) {
    return request
      .post(this.actionUrl())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.identifications = {
  uri: '/v3.0/identification',
  url(action) {
    return api.apiRoot(this.uri, action);
  },
  removeIdentification(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.Signing = {
  uri: '/signing',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  strike(guid) {
    return request
      .put(this.url(`${guid}/strike`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  unstrike(guid) {
    return request
      .put(this.url(`${guid}/unstrike`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  saveNotes(guid, notes) {
    return request
      .put(this.url(`${guid}/notes`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(notes)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  get(guid, metaData) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdl);
  }
};

api.SigningPublic = {
  uri: '/v2.0/signing/public',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  get(guid) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(res =>
        apiHelper.apiNotaryEntryTranslationHelper([res]).then(() => res)
      )
      .catch(apiHelper.errorHdl);
  },

  exists(guid) {
    return request
      .head(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.DocumentPublic = {
  uri: '/v2.0/journalexport/public/document',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  getVerification(guid) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  exists(guid) {
    return request
      .head(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.NotaryEntry = {
  uri: '/notaryentry',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  saveEntryNotes(guid, notes) {
    return request
      .put(this.url(`${guid}/notes`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(notes)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  get(guid) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.UserOath = {
  uri: '/useroath',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  get(guid) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },
  getAll() {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },
  save(Statement) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({ Statement: Statement })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  update(GUID, Statement) {
    return request
      .put(this.url(GUID))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({ Statement: Statement })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  remove(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.Statement = {
  uri: '/statement',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  get(guid) {
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  save(Statement) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({ Statement: Statement })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  remove(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

api.UserSignature = {
  uri: '/usersignature',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  get(guid, includeData) {
    let qs = {};
    if (includeData) {
      qs.inclsub = true;
      qs.incldata = true;
    }
    return request
      .get(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .query(qs)
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  },

  save(data) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  remove(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

//==================Api WebApp Specific

api.Reports = {
  uri: '/v2.0/report',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  signing(Start, End, Fidelity) {
    return request
      .put(this.url('signing'))
      .query({})
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({
        CreatedAfter: moment(Start).utc().format(),
        CreatedBefore: moment(End).utc().format(),
        Fidelity: Fidelity
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Stripe = {
  uri: '/stripe',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  publicKey() {
    return request
      .get(this.url('publickey'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Boarding = {
  uri: '/setup',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  setup(
    first_name,
    last_name,
    email,
    pwd,
    commissioned_name,
    commission_state,
    commission_number,
    commission_province,
    m_license_exp_date,
    leadSource,
    campaignId,
    telephone,
    SmsOptIn,
    localtimezone,
    IsDefault
  ) {
    let userMaster = {
      FirstName: first_name,
      LastName: last_name,
      Email: email,
      Password: pwd,
      Telephone: telephone,
      SmsOptIn: SmsOptIn ? moment().utc().format() : null,
      LocalTimezone: localtimezone,
      Profiles: [
        {
          CommissionedName: commissioned_name,
          CommissionState: commission_state,
          CommissionProvince: commission_province,
          CommissionNumber: commission_number,
          ExpirationDate: m_license_exp_date
            ? m_license_exp_date.format()
            : null,
          LocalTimezone: localtimezone,
          IsDefault: IsDefault
        }
      ]
    };

    if (leadSource) {
      userMaster.LeadSource = leadSource;
    }
    if (campaignId) {
      userMaster.CampaignId = campaignId;
    }

    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(userMaster)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Organization = {
  uri: '/organization/signup',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  signup(payload) {
    const organizationPayload = {
      ...payload
    };

    if (!organizationPayload.LeadSource) {
      delete organizationPayload.LeadSource;
    }

    if (!organizationPayload.CampaignId) {
      delete organizationPayload.CampaignId;
    }

    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(organizationPayload)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.OrganizationUserInvite = {
  uri: '/signup/org/user',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  getInvite(token) {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .query({ token })
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  signup(payload) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(payload)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.ValidateEmail = {
  uri: '/email/validate',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  validate(email) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({ Email: email })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Verify = {
  uri: '/verify',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  startEmail() {
    return request
      .post(this.url('email/start'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  startSMS() {
    return request
      .post(this.url('sms/start'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  finishEmail(code) {
    return request
      .post(this.url('email/finish'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({
        Code: code
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  finishSMS(code, SmsOptIn) {
    return request
      .post(this.url('sms/finish'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({
        Code: code,
        SmsOptIn: SmsOptIn
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.SubscriptionTier = {
  uri: '/v2.0/subscriptiontier',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  testCoupon(couponCode) {
    return request
      .get(this.url())
      .query({ coupon: couponCode })
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  get() {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.JournalExport = {
  uri: '/v2.0/journalexport',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  create(bodyData, Name, AdditionalData) {
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({ Name: Name, AdditionalData: AdditionalData, Body: bodyData })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  get(guid, inclData) {
    let qs = {};
    if (inclData) {
      qs.inclData = true;
    }
    return request
      .get(this.url(guid))
      .query(qs)
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  getAll(includeData) {
    let qs = {};
    if (includeData) {
      qs.includeData = true;
    }
    return request
      .get(this.url())
      .query(qs)
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  generate(data, isBlob = false) {
    return request
      .post(this.url('generate'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .responseType(isBlob ? 'blob' : 'json')
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  download(guid) {
    return request
      .get(this.url(`${guid}/download`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .responseType('blob')
      .then(res => res.body)
      .catch(apiHelper.errorHdl);
  },

  delete(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.PaymentCardV2 = {
  uri: '/v2.0/paymentcard',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  create(payment_method_id) {
    let req = {
      StripePaymentMethodId: payment_method_id
    };

    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  markDefaultMethod(payment_method_id) {
    let req = {
      IsDefaultPaymentMethod: true
    };

    return request
      .put(this.url(payment_method_id))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  remove(guid) {
    let req = {};
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send(req)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  markPrimaryAndBackupMethod(payment_method_id, data) {
    return request
      .put(this.url(payment_method_id))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.SubscriptionTier = {
  uri: '/v2.0/subscriptiontier',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  testCoupon(couponCode) {
    return request
      .get(this.url())
      .query({ coupon: couponCode })
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  get() {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.SigningDraft = {
  uri: '/v2.0/signingdraft',

  url(action) {
    return api.apiRoot(this.uri, action);
  },

  get(guid) {
    return (
      request
        .get(this.url(guid))
        // inclsub is a no-op on the v2 handler today (it only honors incldata),
        // but is sent so the same call is forward-compatible with the v4 piecewise API.
        .query({ inclsub: true, incldata: true })
        .timeout({ response: c.api.timeout_ms })
        .set(api.globalHeaders())
        .retry(c.api.retry_count)
        .send({})
        .then(apiHelper.responseHdl)
        .catch(apiHelper.errorHdl)
    );
  },

  create(data) {
    let draftData = {
      AdditionalData: data.draft_id,
      Body: utils.encodeObjectForBlobData(data)
    };
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send(draftData)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  update(guid, data, timeoutMs = c.api.timeout_ms) {
    return request
      .put(this.url(guid))
      .timeout({ response: timeoutMs })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  async finalise(
    guid,
    requestGUID,
    userMasterGuid,
    deviceGUID,
    mStartDate,
    mCompletedDate,
    signers,
    locationInfo,
    notarizationDetails,
    notarizationDocuments,
    documents,
    fingerprints,
    identifications,
    pictures,
    signatures,
    witnesses,
    emailRecipients,
    auditLogArray,
    signingTimestamp,
    localTimezone,
    userProfileGUID,
    type
  ) {
    let qs = {};
    qs.finalize = '';
    qs.inclsub = '';
    qs.incldata = '';
    let signingData = utils.encodeObjectForBlobData(
      draftEntryHelper.getSigningFromDraft(
        requestGUID,
        userMasterGuid,
        deviceGUID,
        mStartDate,
        mCompletedDate,
        signers,
        locationInfo,
        notarizationDetails,
        notarizationDocuments,
        documents,
        fingerprints,
        identifications,
        pictures,
        signatures,
        witnesses,
        emailRecipients,
        auditLogArray,
        signingTimestamp,
        localTimezone,
        userProfileGUID,
        type
      )
    );
    let data = {
      AdditionalData: 'Finalize draft'
    };

    // Full signing body upload can exceed the default 30s timeout on large packages.
    await this.update(
      guid,
      {
        AdditionalData: null,
        Body: signingData
      },
      c.api.signing_upload_timeout_ms
    );
    return request
      .put(this.url(guid))
      .timeout({ response: c.api.signing_upload_timeout_ms })
      .query(qs)
      .set(api.globalHeaders())
      .send(data)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  remove(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.setEndpoint = endpoint => {
  api.endpoint = endpoint;
};

api.apiRoot = (uri, action) => {
  return api.endpoint + uri + (action ? `/${action}` : '');
};

api.globalHeaders = () => {
  let headers = {};

  let token = api.token;
  if (token && token.length > 0) {
    headers[c.api.token] = token;
  }

  let device_guid = juratLocalStorage.get(c.storage.logged_in_device_id);
  if (device_guid) {
    headers[c.api.device_guid] = device_guid;
  }

  return headers;
};

api.setToken = token => {
  api.token = token;
};

api.getToken = () => {
  return api.token;
};

api.mergeSigner = {
  uri: '/v3.0/mergesigner',

  url(action) {
    return api.apiRoot(this.uri, action);
  },
  mergeSignerData(mergeSignerData) {
    return request
      .post(this.url())
      .timeout({ response: c.api.signing_upload_timeout_ms }) //timeout_ms
      .set(api.globalHeaders())
      .send(mergeSignerData)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdlNoConnectionHdl);
  }
};

// Organization Admin
api.OrganizationBilling = {
  dashboard() {
    return request
      .get(this.url('dashboard'))
      .set(api.globalHeaders())
      .timeout({ response: c.api.timeout_ms })
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  cycles(filters = {}) {
    return request
      .get(this.url('cycles'))
      .set(api.globalHeaders())
      .query(filters)
      .timeout({ response: c.api.timeout_ms })
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  auditEvents(filters = {}) {
    return request
      .get(this.url('audit-events'))
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .query(filters)
      .timeout({ response: c.api.timeout_ms })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  download(path, filters = {}) {
    return request
      .get(this.url(path))
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .query(filters)
      .responseType('blob')
      .timeout({ response: c.api.timeout_ms })
      .then(response => response.body)
      .catch(apiHelper.errorHdl);
  },
  uri: '/tenant/billing',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  profile() {
    return request
      .get(this.url('profile'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  hostedAccounts(indexStart = 0, maxResults = 100, search = '') {
    return request
      .get(this.url('hosted-accounts'))
      .query({
        indexstart: indexStart,
        maxresults: maxResults,
        ...(search ? { search } : {})
      })
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  hostedDeletionPreview(guid) {
    return request
      .post(
        this.url(`hosted-accounts/${encodeURIComponent(guid)}/deletion/preview`)
      )
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  requestHostedDeletion(guid, idempotencyKey) {
    return request
      .post(this.url(`hosted-accounts/${encodeURIComponent(guid)}/deletion`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .set('Idempotency-Key', idempotencyKey)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  pricingPreview() {
    return request
      .get(this.url('pricing-preview'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  sponsorships(
    indexStart = 0,
    maxResults = 100,
    search = '',
    historyOnly = false
  ) {
    return request
      .get(this.url('sponsorships'))
      .query({
        indexstart: indexStart,
        maxresults: maxResults,
        ...(search ? { search } : {}),
        ...(historyOnly ? { historyonly: true } : {})
      })
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  sponsorshipCandidates(indexStart = 0, maxResults = 100, search = '') {
    return request
      .get(this.url('sponsorships/candidates'))
      .query({
        indexstart: indexStart,
        maxresults: maxResults,
        ...(search ? { search } : {})
      })
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  sponsorshipPreview(notaryUserMasterGUID, frequency) {
    return request
      .post(this.url('sponsorships/preview'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({
        NotaryUserMasterGUID: notaryUserMasterGUID,
        Frequency: frequency
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  startSponsorship(notaryUserMasterGUID, frequency, idempotencyKey) {
    return request
      .post(this.url('sponsorships'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .set('Idempotency-Key', idempotencyKey)
      .send({
        NotaryUserMasterGUID: notaryUserMasterGUID,
        Frequency: frequency
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  groupSponsorshipPreview(notaryUserMasterGUIDs, frequency) {
    return request
      .post(this.url('sponsorships/group/preview'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({
        NotaryUserMasterGUIDs: notaryUserMasterGUIDs,
        Frequency: frequency
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  startGroupSponsorship(notaryUserMasterGUIDs, frequency, idempotencyKey) {
    return request
      .post(this.url('sponsorships/group'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .set('Idempotency-Key', idempotencyKey)
      .send({
        NotaryUserMasterGUIDs: notaryUserMasterGUIDs,
        Frequency: frequency
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  endSponsorship(guid, idempotencyKey) {
    return request
      .post(this.url(`sponsorships/${encodeURIComponent(guid)}/end`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .set('Idempotency-Key', idempotencyKey)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  paymentMethods() {
    return request
      .get(this.url('payment-methods'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  createSetupIntent(idempotencyKey) {
    return request
      .post(this.url('payment-methods/setup-intent'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .set('Idempotency-Key', idempotencyKey)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  confirmPaymentMethod(setupIntentId) {
    return request
      .post(this.url('payment-methods/confirm'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({ SetupIntentId: setupIntentId })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  setRole(guid, idempotencyKey, role) {
    return request
      .put(this.url(`payment-methods/${encodeURIComponent(guid)}/role`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .set('Idempotency-Key', idempotencyKey)
      .send(role)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  setDefault(guid, idempotencyKey) {
    return request
      .put(this.url(`payment-methods/${encodeURIComponent(guid)}/default`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .set('Idempotency-Key', idempotencyKey)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  detach(guid, idempotencyKey) {
    return request
      .delete(this.url(`payment-methods/${encodeURIComponent(guid)}`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .set('Idempotency-Key', idempotencyKey)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  invoices(indexStart = 0, maxResults = 10, filters = {}) {
    return request
      .get(this.url('invoices'))
      .query({ indexStart, maxResults, ...filters })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  invoice(guid) {
    return request
      .get(this.url('invoices/' + encodeURIComponent(guid)))
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  payInvoice(invoiceGuid, paymentMethodGuid = null, idempotencyKey = null) {
    let req = request
      .post(this.url(`invoices/${encodeURIComponent(invoiceGuid)}/pay`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders());
    if (idempotencyKey) {
      req = req.set('Idempotency-Key', idempotencyKey);
    }
    return req
      .send({ PaymentMethodGUID: paymentMethodGuid })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Users = {
  uri: '/v1.0/users',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  search(email) {
    return request
      .get(this.url('search'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .query({ email })
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.ShareRequest = {
  uri: '/sharerequest',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  list(params = {}) {
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .query(params)
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  create(payload) {
    const body = typeof payload === 'string' ? { Email: payload } : payload;
    return request
      .post(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send(body)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  cancel(guid) {
    return request
      .post(this.url(`${guid}/cancel`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  approve(guid, payload) {
    const req = request
      .post(this.url(`${guid}/approve`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders());

    const hasBody =
      payload && typeof payload === 'object' && Object.keys(payload).length > 0;

    if (hasBody) {
      req.send(payload);
    }

    return req
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  reject(guid) {
    return request
      .post(this.url(`${guid}/reject`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.RepositoryNotaries = {
  uri: '/repository/notaries',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  auditLog(notaryUserGuid) {
    const safeGuid = encodeURIComponent(String(notaryUserGuid));
    return request
      .get(this.url(`${safeGuid}/auditlog`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.RepositoryUsers = {
  uri: '/repository/users',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  listPaged(params = {}) {
    const { indexstart, maxresults, order, text } = params || {};
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .query({
        ...(indexstart !== undefined ? { indexstart } : {}),
        ...(maxresults !== undefined ? { maxresults } : {}),
        ...(order ? { order } : {}),
        ...(text ? { text } : {})
      })
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Associations = {
  uri: '/repository/associations',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  remove(guid) {
    return request
      .delete(this.url(guid))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.RepositorySignings = {
  uri: '/repository/signings',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  listByUserGuid(params = {}) {
    const {
      userGuid,
      indexstart,
      maxresults,
      order,
      inclsub,
      incldata,
      search,
      createdafter,
      createdbefore
    } = params || {};
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .query({
        ...(userGuid ? { userGuid } : {}),
        ...(indexstart !== undefined ? { indexstart } : {}),
        ...(maxresults !== undefined ? { maxresults } : {}),
        ...(order ? { order } : {}),
        ...(inclsub !== undefined ? { inclsub } : {}),
        ...(incldata !== undefined ? { incldata } : {}),
        ...(search ? { text: search } : {}),
        ...(createdafter ? { createdafter } : {}),
        ...(createdbefore ? { createdbefore } : {})
      })
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },

  get(guid, includeData) {
    let qs = {};
    if (includeData) {
      qs.incldata = true;
      qs.inclsub = true;
    }
    return request
      .get(this.url(guid))
      .query(qs)
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .send({})
      .then(apiHelper.responseHdl)
      .then(apiHelper.apiNotaryEntryTranslationHelper)
      .catch(apiHelper.errorHdl);
  }
};

api.NotaryAssociations = {
  uri: '/notary/associations',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  list(params = {}) {
    const { skip, maxresults } = params || {};
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .query({
        ...(skip !== undefined ? { skip } : {}),
        ...(maxresults !== undefined ? { maxresults } : {})
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.NotaryShareEntries = {
  uri: '/notary/shareentries',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  share(adminGuid, entryGuids) {
    return request
      .post(this.url())
      .set(api.globalHeaders())
      .send({
        AdminGuid: adminGuid,
        EntryGuids: entryGuids
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

api.Inbox = {
  uri: '/inbox',

  url(action) {
    return api.apiRoot(this.uri, action || '');
  },

  list(params = {}) {
    const { indexstart, maxresults, order, status } = params || {};
    return request
      .get(this.url())
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .retry(c.api.retry_count)
      .query({
        ...(indexstart !== undefined ? { indexstart } : {}),
        ...(maxresults !== undefined ? { maxresults } : {}),
        ...(order ? { order } : {}),
        ...(status ? { status } : {})
      })
      .send({})
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};

export default api;

api.NotificationInbox = {
  list({ indexStart = 0, maxResults = 20, unreadOnly = false } = {}) {
    return request
      .get(api.apiRoot('/v3.0/notification-inbox', ''))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .query({
        indexstart: indexStart,
        maxresults: maxResults,
        unreadonly: unreadOnly
      })
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  markRead(guid) {
    return request
      .post(api.apiRoot('/v3.0/notification-inbox', `${guid}/read`))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  },
  markAllRead() {
    return request
      .post(api.apiRoot('/v3.0/notification-inbox', 'read-all'))
      .timeout({ response: c.api.timeout_ms })
      .set(api.globalHeaders())
      .then(apiHelper.responseHdl)
      .catch(apiHelper.errorHdl);
  }
};
